/**
 * Provisioning — SCIM 2.0: the base URL and a bearer token to give the
 * identity provider, and whether the people it adds get a seat.
 *
 * A token is shown once, in full, the moment it is made, and never again: the
 * Worker keeps only its digest. The list says when each was last used, which
 * is how an administrator tells a live integration from a forgotten one.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Callout, PrimaryButton, Row, RowAction, StatusCell, Table } from '../../components/primitives';
import { SettingToggle } from '../../components/settings-row';
import { useConfirm } from '../../components/Modal';
import { api, apiPath, prefix } from '../../lib/api';
import { isoDay } from '../../lib/format';
import type { OrgIdentity, ScimToken } from '../../lib/types';
import type { SectionProps } from './Security';

export function ScimSection({ organization, identity, apply }: SectionProps): React.JSX.Element {
  const [issued, setIssued] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();
  const base = apiPath`/orgs/${organization.id}/identity`;

  const issue = (): void =>
    void apply(async () => {
      const answer = await api.post<OrgIdentity & { issued: { token: string } }>(apiPath`${prefix(base)}/scim-tokens`, { label: 'Identity provider' });
      setIssued(answer.issued.token);
      return answer;
    });

  const revoke = async (token: ScimToken): Promise<void> => {
    const agreed = await ask({
      title: 'Revoke this token?',
      lede: 'Your identity provider stops provisioning with it at once. Nobody already here is changed.',
      confirm: 'Revoke',
      danger: true
    });
    if (agreed) void apply(() => api.del<OrgIdentity>(apiPath`${prefix(base)}/scim-tokens/${token.id}`));
  };

  const columns = '1.2fr 110px 110px 100px 80px';
  // Tokens are the owner's (the Worker answers an administrator 403): a token
  // makes members of anybody whose address it names. The seat policy below is
  // an administrator's like the rest of the page.
  const owner = organization.role === 'owner';

  return (
    <>
      {dialog}
      <div style={{ display: 'grid', gridTemplateColumns: '190px minmax(0,1fr)', gap: 12, alignItems: 'baseline' }}>
        <span style={{ fontSize: 13, color: color.inkSubtle }}>SCIM base URL</span>
        <span style={{ fontFamily: font.mono, fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>{identity.scim.baseUrl}</span>
      </div>

      {issued && (
        <Callout title="Copy this token now — it is not shown again">
          <span style={{ fontFamily: font.mono, fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>{issued}</span>
        </Callout>
      )}

      <Table
        columns={columns}
        header={['Token', 'Made', 'Last used', 'State', '']}
        toolbar={
          owner ? (
            <PrimaryButton tone="ink" size="sm" onClick={issue}>
              New token
            </PrimaryButton>
          ) : (
            <span style={{ fontSize: 13, color: color.inkSubtle }}>Only the owner makes and revokes tokens.</span>
          )
        }
      >
        {identity.scim.tokens.length === 0 ? (
          <Row columns={columns}>
            <span style={{ color: color.inkFaint }}>
              {owner ? 'No token yet. Make one and give it to your identity provider.' : 'No token yet.'}
            </span>
          </Row>
        ) : (
          identity.scim.tokens.map((token) => (
            <Row key={token.id} columns={columns}>
              <span>{token.label}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{isoDay(token.createdAt)}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{token.lastUsedAt ? isoDay(token.lastUsedAt) : '—'}</span>
              {token.revokedAt ? <StatusCell tone="off" word="revoked" ring /> : <StatusCell tone="ok" word="live" />}
              {token.revokedAt || !owner ? (
                <span />
              ) : (
                <RowAction danger onClick={() => void revoke(token)}>
                  Revoke
                </RowAction>
              )}
            </Row>
          ))
        )}
      </Table>

      <label style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14 }}>
        <SettingToggle
          on={identity.policy.scimAssignSeat}
          onToggle={() => void apply(() => api.patch<OrgIdentity>(apiPath`${prefix(base)}/policy`, { scimAssignSeat: !identity.policy.scimAssignSeat }))}
        />
        Give people your identity provider adds a seat, if one is free
      </label>
      <p style={{ margin: 0, fontSize: 13, color: color.inkSubtle, lineHeight: 1.6, maxWidth: 640 }}>
        Deactivating someone in your directory deactivates them here and takes their seat back. Deleting them does the same and
        keeps what they published. Groups become teams.
      </p>
    </>
  );
}
